"use client";

import { motion } from "framer-motion";

import { FetchTooltip } from "./FetchTooltip";

interface Tab {
  id: string;
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  count?: number;
  reserveCountSpace?: boolean;
  /** Shown in a hover tooltip — for a tab whose label is abbreviated for space. */
  tooltip?: string;
}

interface FetchTabBarProps {
  tabs: Tab[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  size?: "default" | "sm" | "xs";
  variant?: "underline" | "pill" | "slider";
  layoutId?: string;
  responsive?: boolean;
  /** Underline variant only: below the `sm` breakpoint, show one dropdown listing every tab instead of a row
   *  that has to be scrolled sideways. The normal bar returns from `sm` up. */
  mobileSelect?: boolean;
}

export const FetchTabBar = ({
  tabs,
  activeTab,
  onTabChange,
  size = "default",
  variant = "underline",
  layoutId = "fetchTabBarSlider",
  responsive = false,
  mobileSelect = false,
}: FetchTabBarProps) => {
  const isCompact = size === "xs";
  const paddingClasses = isCompact ? "px-2.5 py-1" : size === "sm" ? "px-3 py-1.5" : "px-4 py-2";
  const textSizeClasses = isCompact ? "text-[13px]" : size === "sm" ? "text-sm" : "";
  const marginBottomClasses = size === "sm" || isCompact ? "mb-2" : "mb-4";
  const countClasses = isCompact ? "px-1 py-0 text-[11px]" : "px-1.5 py-0.5 text-xs";

  const labelClass = responsive ? "hidden sm:inline" : "";

  const renderCount = (tab: Tab, className = ""): React.ReactNode => {
    if (tab.count === undefined && !tab.reserveCountSpace) return null;
    const isPlaceholder = tab.count === undefined;
    return (
      <span
        aria-hidden={isPlaceholder ? "true" : undefined}
        className={`rounded border border-gray-200 bg-gray-100 font-bold text-gray-500 ${countClasses} ${
          isPlaceholder ? "invisible" : ""
        } ${className}`}
      >
        {tab.count ?? 0}
      </span>
    );
  };

  const wrapWithTooltip = (tab: Tab, button: React.ReactNode): React.ReactNode =>
    tab.tooltip ? (
      <FetchTooltip key={tab.id} content={tab.tooltip}>
        {button}
      </FetchTooltip>
    ) : (
      button
    );

  if (variant === "slider") {
    return (
      <nav className="inline-flex" aria-label="Tabs">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;

          const button = (
            <button
              key={tab.id}
              type="button"
              onClick={() => !tab.disabled && onTabChange(tab.id)}
              disabled={tab.disabled}
              aria-label={responsive && tab.icon ? tab.label : undefined}
              aria-pressed={isActive}
              className={`
                relative flex gap-2 items-center py-3 px-4 font-medium text-sm transition-colors whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-1
                ${
                  tab.disabled
                    ? "text-gray-300 cursor-not-allowed"
                    : isActive
                      ? "text-gray-900"
                      : "text-gray-400 [@media(hover:hover)]:hover:text-gray-600"
                }
              `}
            >
              {tab.icon}
              <span className={labelClass}>{tab.label}</span>

              {renderCount(tab)}

              {/* Gray underline (always visible) */}
              <div className="absolute left-0 right-0 bottom-0 h-0.5 bg-gray-200" />

              {/* Indigo underline (active tab) */}
              {isActive && (
                <motion.div
                  layoutId={layoutId}
                  className="absolute left-0 right-0 bottom-0 h-0.5 bg-indigo-400"
                  initial={false}
                  transition={{ duration: 0.2, ease: "easeInOut" }}
                />
              )}
            </button>
          );
          return wrapWithTooltip(tab, button);
        })}
      </nav>
    );
  }

  if (variant === "pill") {
    return (
      <div className="flex gap-0.5 items-center">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;

          const button = (
            <button
              key={tab.id}
              type="button"
              onClick={() => !tab.disabled && onTabChange(tab.id)}
              disabled={tab.disabled}
              aria-label={responsive && tab.icon ? tab.label : undefined}
              aria-pressed={isActive}
              className={`
                flex items-center rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-1 ${isCompact ? "gap-1 px-1.5 py-1 text-[13px]" : "gap-1.5 px-2 py-1 text-sm"}
                ${
                  tab.disabled
                    ? "text-gray-300 cursor-not-allowed"
                    : isActive
                      ? "bg-gray-50 text-gray-600"
                      : "text-gray-500 [@media(hover:hover)]:hover:text-gray-700"
                }
              `}
            >
              {tab.icon}
              <span className={labelClass}>{tab.label}</span>

              {renderCount(tab)}
            </button>
          );
          return wrapWithTooltip(tab, button);
        })}
      </div>
    );
  }

  const bar = (
    <div className={`flex border-b ${marginBottomClasses} flex-shrink-0 ${mobileSelect ? "max-sm:hidden" : ""}`}>
      {tabs.map((tab) => {
        const button = (
          <button
            key={tab.id}
            type="button"
            onClick={() => !tab.disabled && onTabChange(tab.id)}
            disabled={tab.disabled}
            aria-label={responsive && tab.icon ? tab.label : undefined}
            aria-pressed={activeTab === tab.id}
            className={`${paddingClasses} ${textSizeClasses} font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-1 ${
              activeTab === tab.id
                ? "border-b-2 border-indigo-500 text-indigo-600"
                : tab.disabled
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-gray-600 [@media(hover:hover)]:hover:text-gray-800"
            }`}
          >
            <span className="inline-flex items-center gap-1.5">
              {tab.icon}
              <span className={labelClass}>{tab.label}</span>
            </span>
            {renderCount(tab, "ml-1.5")}
          </button>
        );
        return wrapWithTooltip(tab, button);
      })}
    </div>
  );

  if (!mobileSelect) return bar;

  return (
    <>
      <div className={`sm:hidden ${marginBottomClasses}`}>
        <label className="sr-only" htmlFor={`${layoutId}-mobile-select`}>
          Section
        </label>
        <select
          id={`${layoutId}-mobile-select`}
          value={activeTab}
          onChange={(e) => onTabChange(e.target.value)}
          className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-3 text-base font-medium text-gray-900"
        >
          {tabs.map((tab) => (
            <option key={tab.id} value={tab.id} disabled={tab.disabled}>
              {tab.label}
              {tab.count !== undefined ? ` (${tab.count})` : ""}
            </option>
          ))}
        </select>
      </div>
      {bar}
    </>
  );
};
